import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, User } from "lucide-react";
import { fetchBlogPost } from "../lib/api";
import Seo from "../components/Seo";

const BASE_URL = "https://globaltreats.in";
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchBlogPost(slug)
      .then((p) => { if (active) setPost(p); })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-[#B07A5C]">Loading article...</div>;

  if (notFound || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <Seo path={`/blog/${slug}`} title="Article not found" noindex />
        <h1 className="font-display text-3xl font-bold text-[#3A2417]">Article not found</h1>
        <Link to="/blog" className="inline-block mt-4 text-[#D46A8A]">← Back to the journal</Link>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <Seo
        path={`/blog/${post.slug}`}
        title={post.title}
        description={post.excerpt}
        image={post.cover_image || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.cover_image,
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: { "@type": "Organization", name: post.author || "Globaltreats" },
          publisher: { "@type": "Organization", name: "Globaltreats", logo: { "@type": "ImageObject", url: `${BASE_URL}/assets/globaltreats-logo-gold.png` } },
          mainEntityOfPage: `${BASE_URL}/blog/${post.slug}`,
        }}
      />
      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-[#B07A5C] hover:text-[#D46A8A] mb-6"><ArrowLeft size={16} /> Back to the journal</Link>

      <h1 className="font-display text-3xl md:text-4xl font-bold text-[#3A2417] leading-tight">{post.title}</h1>
      <div className="flex flex-wrap items-center gap-4 text-sm text-[#B07A5C] mt-4">
        <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} /> {fmtDate(post.created_at)}</span>
        <span className="inline-flex items-center gap-1.5"><User size={15} /> {post.author || "Globaltreats"}</span>
      </div>

      {post.cover_image && (
        <div className="rounded-3xl overflow-hidden bakery-shadow mt-6 aspect-[16/9] bg-[#F6E7DA]">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="blog-content mt-8" data-testid="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />

      <div className="mt-12 rounded-3xl bg-gradient-to-br from-[#FCEFE3] to-[#F7DCE4] p-8 text-center">
        <h3 className="font-display text-2xl font-bold text-[#3A2417]">Craving cake now?</h3>
        <p className="text-[#6B4226] mt-2">Order freshly baked cakes online with next-day delivery across Noida & Greater Noida.</p>
        <Link to="/collections/all" className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors mt-5">Browse Cakes</Link>
      </div>
    </article>
  );
};

export default BlogPost;
